import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RankXPName, StakingStartRank } from "@/constants/rank";
import {
  adjustStake,
  approveRankStakeContract,
  getMissingAmount,
  getReductionPercents,
  getUnstakeCooldown,
  getUserStakingInfo,
  isUserRankActive,
  stake,
  unstake,
} from "@/helper/contractFunctions/MafiaRankStake";

export interface StakeReduction {
  familyShare: { current: number; max: number };
  equippedBuildings: { current: number; max: number };
  equippedMafia: { current: number; max: number };
  totalReduction: number;
}

export interface RankActivationState {
  isRankActive: boolean;
  userStakingInfo: UserStakingInfo;
  stakeReduction: StakeReduction;
  isStaked: boolean[];
  nextStakeRank: number;
  isStaking: boolean;
  isUnstaking: boolean;
  isAdjustingStake: boolean;
  isLoadingStakeData: boolean;
  unstakeCooldown: number;
  isLoadingUnstakeCooldown: boolean;

  missingStakeAmount: number;
  isLoadingMissingStakeAmount: boolean;
}

export interface UserStakingInfo {
  stakes: Stake[];
  currentStakeLevel: number;
  totalAmount: number;
  lastUnstakeTime: number;
}

export interface Stake {
  rankLevel: number;
  amount: number;
  reductionPercent: number;
  timestamp: number;
}

const initialState: RankActivationState = {
  isRankActive: false,
  userStakingInfo: {
    stakes: [],
    currentStakeLevel: 0,
    totalAmount: 0,
    lastUnstakeTime: 0,
  },
  stakeReduction: {
    familyShare: { current: 0, max: 40 },
    equippedBuildings: { current: 0, max: 25 },
    equippedMafia: { current: 0, max: 25 },
    totalReduction: 0,
  },
  isStaked: [],
  nextStakeRank: 0,
  isStaking: false,
  isUnstaking: false,
  isAdjustingStake: false,
  isLoadingStakeData: false,
  unstakeCooldown: 0,
  isLoadingUnstakeCooldown: false,

  missingStakeAmount: 0,
  isLoadingMissingStakeAmount: false,
};

export const getUnstakeCooldownAction = createAsyncThunk(
  "rankActivation/getUnstakeCooldown",
  async () => {
    const unstakeCooldown = await getUnstakeCooldown();
    return Number(unstakeCooldown);
  }
);

export const getMissingStakeAmountAction = createAsyncThunk(
  "rankActivation/getMissingStakeAmount",
  async ({ account }: { account: string }) => {
    const missingStakeAmount = await getMissingAmount(account);
    return Number(missingStakeAmount);
  }
);

// Load user staking info first to determine initial state
export const loadUserStakingInfoAction = createAsyncThunk(
  "rankActivation/loadUserStakingInfo",
  async ({ account }: { account: string }) => {
    const userStakingInfo = await getUserStakingInfo(account);
    return userStakingInfo;
  }
);

export const loadStakeDataAction = createAsyncThunk(
  "rankActivation/loadStakeData",
  async ({ account }: { account: string }) => {
    // Load rank active status
    const rankActive = await isUserRankActive(account);

    // Load reduction percents
    const reductionPercents = await getReductionPercents(account);

    // Calculate total reduction
    const totalReduction =
      reductionPercents.familyReductionPercent +
      reductionPercents.equipmentReductionPercent +
      reductionPercents.buildingReductionPercent;

    return {
      isRankActive: Boolean(rankActive),
      stakeReduction: {
        familyShare: {
          current: reductionPercents.familyReductionPercent,
          max: 40,
        },
        equippedBuildings: {
          current: reductionPercents.buildingReductionPercent,
          max: 25,
        },
        equippedMafia: {
          current: reductionPercents.equipmentReductionPercent,
          max: 25,
        },
        totalReduction: totalReduction,
      },
    };
  }
);

export const stakeAction = createAsyncThunk(
  "rankActivation/stake",
  async ({
    account,
    stakeAmount,
  }: {
    account: string;
    stakeAmount: number;
  }) => {
    // First, approve the contract to spend MAFIA tokens
    await approveRankStakeContract(account, stakeAmount);

    // Then, stake the amount
    await stake(account);
  }
);

export const unstakeAction = createAsyncThunk(
  "rankActivation/unstake",
  async ({ account }: { account: string }) => {
    await unstake(account);
  }
);

export const adjustStakeAction = createAsyncThunk(
  "rankActivation/adjustStake",
  async ({
    account,
    stakeAmount,
  }: {
    account: string;
    stakeAmount: number;
  }) => {
    // First, approve the contract to spend MAFIA tokens if needed
    if (stakeAmount > 0) {
      await approveRankStakeContract(account, stakeAmount);
    }

    // Then, adjust the stake
    await adjustStake(account);
  }
);

export const rankActivationSlice = createSlice({
  name: "rankActivation",
  initialState,
  reducers: {
    setIsStaked: (state, action) => {
      state.isStaked = action.payload;
    },
    setNextStakeRank: (state, action) => {
      state.nextStakeRank = action.payload;
    },
    resetRankActivation: () => initialState,
  },
  extraReducers: (builder) => {
    // Handle loading user staking info first
    builder.addCase(loadUserStakingInfoAction.pending, (state) => {
      state.isLoadingStakeData = true;
    });
    builder.addCase(
      loadUserStakingInfoAction.fulfilled,
      (state, { payload }) => {
        state.userStakingInfo = payload;
        state.isLoadingStakeData = false;

        // Calculate isStaked array and nextStakeRank based on stakes
        const totalRanks = RankXPName.length;
        const isStakedArray = new Array(totalRanks).fill(false);

        if (payload.stakes.length === 0) {
          // No stakes yet - start from Cloak (rank index 3)
          // Ranks 0-2 (Nobody, Apprentice, Pickpocket) don't require staking
          // So they are considered "staked" (no action needed)
          isStakedArray[0] = true; // Nobody
          isStakedArray[1] = true; // Apprentice
          isStakedArray[2] = true; // Pickpocket
          state.nextStakeRank = StakingStartRank; // Cloak
        } else {
          // User has stakes - determine which ranks are staked
          // Mark each rank that has a stake as staked
          payload.stakes.forEach((stake: Stake) => {
            const rankLevel = stake.rankLevel;
            if (rankLevel >= 0 && rankLevel < totalRanks) {
              isStakedArray[rankLevel - 1] = true;
            }
          });

          // Ranks 0-2 (Nobody, Apprentice, Pickpocket) don't require staking
          // So they are considered "staked" (no action needed)
          isStakedArray[0] = true; // Nobody
          isStakedArray[1] = true; // Apprentice
          isStakedArray[2] = true; // Pickpocket

          // Find the next rank that needs staking
          const nextRank = isStakedArray.indexOf(false);
          state.nextStakeRank = nextRank >= 0 ? nextRank : -1;
        }

        state.isStaked = isStakedArray;
      }
    );
    builder.addCase(loadUserStakingInfoAction.rejected, (state) => {
      state.isLoadingStakeData = false;
    });

    // Handle loading additional stake data (rank active status, reduction percents)
    builder.addCase(loadStakeDataAction.pending, (state) => {
      // Don't set loading state here as we already have userStakingInfo loaded
    });
    builder.addCase(loadStakeDataAction.fulfilled, (state, { payload }) => {
      state.isRankActive = payload.isRankActive;
      state.stakeReduction = payload.stakeReduction;
    });
    builder.addCase(loadStakeDataAction.rejected, (state) => {
      // Handle error if needed
    });

    builder.addCase(stakeAction.pending, (state) => {
      state.isStaking = true;
    });
    builder.addCase(stakeAction.fulfilled, (state) => {
      state.isStaking = false;
    });
    builder.addCase(stakeAction.rejected, (state) => {
      state.isStaking = false;
    });

    builder.addCase(unstakeAction.pending, (state) => {
      state.isUnstaking = true;
    });
    builder.addCase(unstakeAction.fulfilled, (state) => {
      state.isUnstaking = false;
    });
    builder.addCase(unstakeAction.rejected, (state) => {
      state.isUnstaking = false;
    });

    builder.addCase(adjustStakeAction.pending, (state) => {
      state.isAdjustingStake = true;
    });
    builder.addCase(adjustStakeAction.fulfilled, (state) => {
      state.isAdjustingStake = false;
    });
    builder.addCase(adjustStakeAction.rejected, (state) => {
      state.isAdjustingStake = false;
    });

    builder.addCase(getUnstakeCooldownAction.pending, (state) => {
      state.isLoadingUnstakeCooldown = true;
    });
    builder.addCase(
      getUnstakeCooldownAction.fulfilled,
      (state, { payload }) => {
        state.unstakeCooldown = payload;
        state.isLoadingUnstakeCooldown = false;
      }
    );
    builder.addCase(getUnstakeCooldownAction.rejected, (state) => {
      state.isLoadingUnstakeCooldown = false;
    });

    builder.addCase(getMissingStakeAmountAction.pending, (state) => {
      state.isLoadingMissingStakeAmount = true;
    });
    builder.addCase(
      getMissingStakeAmountAction.fulfilled,
      (state, { payload }) => {
        state.missingStakeAmount = payload;
        state.isLoadingMissingStakeAmount = false;
      }
    );
    builder.addCase(getMissingStakeAmountAction.rejected, (state) => {
      state.isLoadingMissingStakeAmount = false;
    });
  },
});

export const { setIsStaked, setNextStakeRank, resetRankActivation } =
  rankActivationSlice.actions;

export default rankActivationSlice.reducer;
